import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { http, TOKEN_KEY } from "@/lib/api";
import { QuestionCard } from "@/components/QuestionCard";

export default function Assessment() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const retake = sp.get("retake");
  const [sections, setSections] = useState(null);
  const [token, setToken] = useState(null);
  const [answers, setAnswers] = useState({});
  const [pos, setPos] = useState({ s: 0, q: -1 });
  const [submitting, setSubmitting] = useState(false);
  const saveTimer = useRef(null);
  const retakeStarted = useRef(false);

  useEffect(() => {
    (async () => {
      const { data } = await http.get("/assessment");
      setSections(data.sections);
      let t = localStorage.getItem(TOKEN_KEY);
      if (retake) {
        if (retakeStarted.current) return;
        retakeStarted.current = true;
        try {
          const { data: n } = await http.post("/sessions", { previous_token: retake });
          localStorage.setItem(TOKEN_KEY, n.token);
          toast.success("New attempt started. Your results will be compared with your previous test.");
        } catch {
          toast.error("That previous result couldn't be found, so we started a fresh test.");
          localStorage.removeItem(TOKEN_KEY);
        }
        nav("/assessment", { replace: true });
        return;
      }
      if (t) {
        try {
          const { data: s } = await http.get(`/sessions/${t}`);
          if (s.status === "completed") { nav(`/results/${t}`); return; }
          const ans = s.answers || {};
          setAnswers(ans);
          const first = data.sections.findIndex((sec) => sec.questions.some((x) => ans[x.id] === undefined));
          setPos({ s: first === -1 ? data.sections.length - 1 : first, q: -1 });
        } catch { t = null; }
      }
      if (!t) {
        const { data: n } = await http.post("/sessions");
        t = n.token;
        localStorage.setItem(TOKEN_KEY, t);
      }
      setToken(t);
    })().catch(() => toast.error("Could not load the assessment. Please refresh."));
  }, [nav, retake]);

  const total = useMemo(() => (sections ? sections.reduce((a, s) => a + s.questions.length, 0) : 122), [sections]);
  const done = Object.keys(answers).length;

  const persist = (next, sectionIdx) => {
    saveTimer.current = http.put(`/sessions/${token}/answers`, { answers: next, current_section: sectionIdx }).catch(() => {});
  };

  if (!sections || !token) {
    return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-[#D96B43]" data-testid="assessment-loading" /></div>;
  }

  const section = sections[pos.s];
  const q = pos.q >= 0 ? section.questions[pos.q] : null;
  const isLastQ = pos.s === sections.length - 1 && pos.q === section.questions.length - 1;

  const answer = (val) => {
    const next = { ...answers, [q.id]: val };
    setAnswers(next);
    persist(next, pos.s);
    if (q.type === "likert" && !isLastQ) setTimeout(() => goNext(), 260);
  };

  const goNext = () => {
    setPos((p) => {
      const sec = sections[p.s];
      if (p.q < sec.questions.length - 1) return { s: p.s, q: p.q + 1 };
      if (p.s < sections.length - 1) return { s: p.s + 1, q: -1 };
      return p;
    });
  };
  const goBack = () => {
    setPos((p) => {
      if (p.q > -1) return { s: p.s, q: p.q - 1 };
      if (p.s > 0) return { s: p.s - 1, q: sections[p.s - 1].questions.length - 1 };
      return p;
    });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await http.put(`/sessions/${token}/answers`, { answers, current_section: pos.s });
      await http.post(`/sessions/${token}/submit`);
      nav(`/results/${token}`);
    } catch {
      toast.error("Submission failed. Please try again.");
      setSubmitting(false);
    }
  };

  const sectionAnswered = section.questions.filter((x) => answers[x.id] !== undefined).length;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-8 pb-16">
      <div className="sticky top-16 z-30 -mx-5 sm:-mx-8 px-5 sm:px-8 py-4 backdrop-blur-xl bg-[#FAF8F5]/85 border-b border-[#1C242B]/10">
        <div className="flex items-center justify-between text-xs font-mono-ui text-slate-500">
          <span data-testid="section-indicator">SECTION {pos.s + 1} / {sections.length}</span>
          <span data-testid="progress-count">{done} / {total} ANSWERED</span>
        </div>
        <div className="mt-3 grid grid-cols-10 gap-1.5">
          {sections.map((s, i) => {
            const n = s.questions.filter((x) => answers[x.id] !== undefined).length;
            return (
              <div key={s.id} className="h-1.5 rounded-full bg-[#1C242B]/10 overflow-hidden" title={s.title}>
                <div className="h-full bg-[#D96B43] transition-[width] duration-500" style={{ width: `${(n / s.questions.length) * 100}%`, opacity: i === pos.s ? 1 : 0.55 }} />
              </div>
            );
          })}
        </div>
      </div>

      <div className="pt-10 min-h-[420px]">
        {q === null ? (
          <div className="fade-up" data-testid="section-intro">
            <p className="overline-label">Section {String(pos.s + 1).padStart(2, "0")} · {section.kind === "preference" ? "Self-reflection, no right answers" : "Has right answers"}</p>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#1C242B]">{section.title}</h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">{section.intro}</p>
            <p className="mt-3 text-sm text-slate-500">{section.questions.length} questions · {sectionAnswered} answered</p>
            <button data-testid="section-start-btn" onClick={goNext}
              className="mt-9 group inline-flex items-center gap-2 rounded-full bg-[#1C242B] text-white px-7 py-3.5 font-semibold hover:bg-[#D96B43] transition-colors">
              {sectionAnswered ? "Continue section" : "Start section"} <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        ) : (
          <div>
            <p className="font-mono-ui text-xs text-slate-400 mb-5" data-testid="question-index"><span className="uppercase">{section.title}</span> · Q{pos.q + 1} / {section.questions.length}</p>
            <QuestionCard key={q.id} q={q} value={answers[q.id]} onAnswer={answer} />
          </div>
        )}
      </div>

      <div className="mt-10 flex items-center justify-between">
        <button data-testid="prev-btn" onClick={goBack} disabled={pos.s === 0 && pos.q === -1}
          className="inline-flex items-center gap-2 rounded-full border border-[#1C242B]/15 px-5 py-2.5 text-sm font-medium hover:bg-white disabled:opacity-30 transition-colors">
          <ArrowLeft size={16} /> Back
        </button>
        {q && !isLastQ && (
          <button data-testid="next-btn" onClick={goNext} disabled={answers[q.id] === undefined}
            className="inline-flex items-center gap-2 rounded-full bg-[#D96B43] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#C05630] disabled:opacity-40 transition-colors">
            Next <ArrowRight size={16} />
          </button>
        )}
        {isLastQ && (
          <button data-testid="submit-assessment-btn" onClick={submit} disabled={submitting || answers[q.id] === undefined}
            className="inline-flex items-center gap-2 rounded-full bg-[#3E6B5C] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#2F5548] disabled:opacity-40 transition-colors">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} See my results
          </button>
        )}
      </div>
      {done < total && isLastQ && (
        <p className="mt-4 text-right text-xs text-slate-500" data-testid="unanswered-warning">{total - done} {total - done === 1 ? "question is" : "questions are"} unanswered (sections: {sections.map((s, i) => (s.questions.some((x) => answers[x.id] === undefined) ? i + 1 : null)).filter(Boolean).join(", ")}). Unanswered items are scored as incorrect / neutral.</p>
      )}
    </div>
  );
}
